// src/components/TradeList.jsx
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { apiUrl } from "../config/api";
import { adminAuthHeaders } from "../utils/authHeaders";

const filterButtons = [
  { key: "all", label: "همه" },
  { key: "approved", label: "تایید شده" },
  { key: "rejected", label: "رد شده" },
];

function TradeList() {
  const ITEMS_PER_PAGE = 10;
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  const fetchTrades = useCallback(async () => {
    try {
      setLoading(true);

      let url = apiUrl("/api/trades/all?limit=100");
      if (filter !== "all") {
        url += `&status=${filter}`;
      }

      const response = await fetch(url, {
        headers: adminAuthHeaders(),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "خطا در بارگذاری معاملات");
      }

      setTrades(data.trades || []);
    } catch (error) {
      console.error("Error fetching trades:", error);
      toast.error(error.message || "خطا در بارگذاری معاملات");
      setTrades([]);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchTrades();
  }, [fetchTrades]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const totalPages = Math.max(1, Math.ceil(trades.length / ITEMS_PER_PAGE));
  const paginatedTrades = trades.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("fa-IR");
  };

  const formatNumber = (value, maximumFractionDigits = 2) => {
    if (value === null || value === undefined || value === "") return "-";

    return Number(value).toLocaleString("fa-IR", {
      minimumFractionDigits: 0,
      maximumFractionDigits,
    });
  };

  const getTradeTypeText = (mode) => (mode === "buy" ? "خرید از ما" : "فروش به ما");

  const getStatusText = (status) => {
    switch (status) {
      case "approved":
        return "تایید شده";
      case "rejected":
        return "رد شده";
      case "pending":
        return "در انتظار";
      default:
        return status || "-";
    }
  };

  const getStatusClass = (status, baseClass) => {
    if (status === "approved") return `${baseClass} approved`;
    if (status === "rejected") return `${baseClass} rejected`;
    return `${baseClass} pending`;
  };

  return (
    <section className="MainPageTradesSection">
      <div className="MainPageSectionHeading">
        <span className="MainPagePanelEyebrow">معاملات</span>
        <h3 className="MainPagePanelTitle">لیست معاملات</h3>
      </div>

      <div className="UserPortalPanel">
        <div className="TredeDetailToolbar">
          <div className="TredeDetailFilters">
            {filterButtons.map((button) => (
              <button
                key={button.key}
                type="button"
                className={filter === button.key ? "active" : ""}
                onClick={() => setFilter(button.key)}
              >
                {button.label}
              </button>
            ))}
          </div>

          <span className="TredeDetailCount">{trades.length} معامله</span>
        </div>

        {loading ? (
          <p className="UserPortalEmpty">در حال دریافت معاملات...</p>
        ) : trades.length === 0 ? (
          <p className="UserPortalEmpty">معامله‌ای برای نمایش وجود ندارد.</p>
        ) : (
          <>
            <div className="UserPortalTableWrap TredeDetailDesktopTable">
              <table className="UserPortalTable">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>کاربر</th>
                    <th>نوع</th>
                    <th>مبلغ کل</th>
                    <th>وزن</th>
                    <th>نرخ پایه</th>
                    <th>وضعیت</th>
                    <th>تاریخ</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTrades.map((trade, index) => {
                    const baseRate = trade.mode === "buy" ? trade.valueBuy : trade.valueSell;

                    return (
                      <tr key={trade.id}>
                        <td data-label="#">{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                        <td data-label="کاربر">{trade.userName || "کاربر"}</td>
                        <td data-label="نوع">{getTradeTypeText(trade.mode)}</td>
                        <td data-label="مبلغ کل">{formatNumber(trade.totalPrice, 0)} تومان</td>
                        <td data-label="وزن">{formatNumber(trade.weight, 4)} گرم</td>
                        <td data-label="نرخ پایه">{formatNumber(baseRate, 0)} تومان</td>
                        <td data-label="وضعیت">
                          <span className={getStatusClass(trade.status, "TredeDetailBadge")}>
                            {getStatusText(trade.status)}
                          </span>
                        </td>
                        <td data-label="تاریخ">{formatDate(trade.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="TredeDetailMobileCards">
              {paginatedTrades.map((trade) => {
                const baseRate = trade.mode === "buy" ? trade.valueBuy : trade.valueSell;
                const statusIcon =
                  trade.status === "approved" ? "✓" : trade.status === "rejected" ? "×" : "•";

                return (
                  <article key={trade.id} className="TredeDetailMobileCard">
                    <div className="TredeDetailMobileTop">
                      <div className="TredeDetailMobileMain">
                        <span className={getStatusClass(trade.status, "TredeDetailMobileStatus")}>
                          {statusIcon}
                        </span>
                        <div className="TredeDetailMobileTypeWrap">
                          <strong className="TredeDetailMobileType">{getTradeTypeText(trade.mode)}</strong>
                          <span className="TredeDetailMobileRate">
                            {trade.userName || "کاربر"} | {formatNumber(baseRate, 0)} تومان
                          </span>
                        </div>
                      </div>

                      <div className="TredeDetailMobileValues">
                        <div>
                          <span>وزن</span>
                          <strong>{formatNumber(trade.weight, 4)} گرم</strong>
                        </div>
                        <div>
                          <span>مبلغ کل</span>
                          <strong>{formatNumber(trade.totalPrice, 0)} تومان</strong>
                        </div>
                      </div>
                    </div>

                    <div className="TredeDetailMobileBottom">
                      <span className="TredeDetailMobileDate">{formatDate(trade.createdAt)}</span>
                    </div>
                  </article>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="TredeDetailPagination">
                <button
                  type="button"
                  className="TredeDetailPageBtn"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  قبلی
                </button>

                <span className="TredeDetailPageInfo">
                  صفحه {currentPage} از {totalPages}
                </span>

                <button
                  type="button"
                  className="TredeDetailPageBtn"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  بعدی
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default TradeList;
